// A.5-P.21 — THE L1 OPENING'S DETAIL DISCLOSURE (owner directive 2026-08-23, verbatim: "giving users feedback on
// dimensions will not help but rather overwhelm them. their care is: is the cv okay or not, where is the problem,
// what to do/how to fix it… raw data is not helpful as the synthesis is. this also applies to the other aspects of
// the evaluation.").
//
// WHAT THIS FILE IS FOR. The legacy opening line (`QualityRead.overall`, rendered at `[data-testid="quality-overall"]`)
// used to serialize the review confidence PLUS eleven domain / presentation band sentences and their §4.6 lines
// into a single paragraph. Sol's reading of it: "serialized output: repeated boilerplate plus eleven label/value
// fields in prose." The opening line is now the two-sentence priority summary and nothing else; the band sentences
// live HERE, behind a closed-by-default `<details>`.
//
// THE COPY DID NOT CHANGE. Every sentence this renders comes from `overviewDetailText`, which is
// `domainBandsLine` + `presentationLine` — the same two builders, unedited, producing the same registered
// `CS-OVERVIEW-BAND-SENTENCE` / `CS-OVERVIEW-NOT-SHOWN-GROUP` / `CS-WHAT-WOULD-CHANGE` / `CS-SATURATION` text in
// the same `DOMAIN_ORDER`. Nothing was rewritten and nothing was dropped; only the default visibility moved. That
// keeps the owner's frozen-display rule intact in the way that matters — the flow, the cards and L2 are untouched
// — while ending the information overload the same owner named.
//
// WHY IT IS A SEPARATE COMPONENT AND NOT A CHANGE TO THE OPENING LINE. `QualityReadView` renders `overall` as a
// plain string in one `<p>`; a string slot cannot hold a disclosure. The legacy view is frozen, so the disclosure
// is its own component that the read surface mounts DIRECTLY BELOW the opening line. It adds no card, no section
// and no control of its own — it is one `<details>`.
//
// PROVENANCE. The text is already-scanned registered copy assembled by the adapter (`copyText` / `fillTemplate`
// drop any string the banned-output registry refuses, fail-closed), so it renders through `SystemText`, the
// chokepoint's renderer for system-authored text that is not itself a template.
import type { RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';
import type { LedgerPayload } from './evidenceIndex';
import { overviewDetailText } from './legacyAdapter';
import { Copy } from './renderContent';
import { scanSystemText } from './bannedOutput';
import { BODY_STYLE, FACTS_DETAILS_STYLE, FACTS_SUMMARY_STYLE } from './styles';

/**
 * The disclosure. Renders NOTHING when there is no band text to disclose (a read whose domains and presentation
 * are all suppressed) — an empty toggle is a promise of content that is not there.
 */
export function OverviewDetail({
  read,
  ledger,
}: {
  read: RubricReadPublicV1;
  ledger: LedgerPayload | null;
}) {
  const text = overviewDetailText(read, ledger);
  // FAIL-CLOSED, the same rule every other renderer on this surface follows: a violation drops the whole body
  // rather than rendering part of it. The adapter already scans each member; this is the join's own check.
  if (text.length === 0 || !scanSystemText(text).ok) return null;
  return (
    <details style={FACTS_DETAILS_STYLE} data-testid="rubric-overview-detail">
      <summary style={FACTS_SUMMARY_STYLE} data-testid="rubric-overview-detail-toggle">
        <Copy id="CS-OVERVIEW-DETAIL-TOGGLE" />
      </summary>
      {/* NO `data-copy-src`. T-COPY-1's DOM sweep reads `data-copy-src` WITH `data-copy-id` as "this node is one
          registered string, and here is which". This body is a JOIN of many registered sentences, so it has no
          single id; stamping `cs` with no id made every leaf here report `data-copy-id="null"` and fail the sweep
          on all seven fixtures. Unattributed is the CORRECT declaration for it — the identical text was
          unattributed when it rode in the legacy opening line, and the sweep validates it exactly as it did
          there, through `isRegisteredText`'s join check: every member of the join must itself be registered. */}
      <p className="measure" style={BODY_STYLE} data-testid="rubric-overview-detail-body">
        {text}
      </p>
    </details>
  );
}
